import { useEffect } from 'react';

const PrivacyPolicy = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return (
    <>
      <title>Privacy Policy - Veridian Vanguard Advisors</title>
      <meta name="robots" content="noindex, nofollow" />
      <link rel="canonical" href={`${siteUrl}/privacy-policy`} />

      <div className="bg-slate-900 text-white min-h-screen font-sans">
        {/* Navigation */}
        <nav className="bg-slate-800/95 backdrop-blur-sm shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center space-x-4">
                <span className="text-2xl font-light tracking-wide">VERIDIAN VANGUARD</span>
              </a>
              <a 
                href="/"
                className="bg-lime-400 text-slate-800 px-6 py-2 rounded-lg font-medium hover:bg-lime-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Home
              </a>
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="space-y-12">
            <div>
              <h1 className="text-5xl font-extralight mb-4">
                PRIVACY <span className="text-lime-400">POLICY</span>
              </h1>
              <p className="text-white/60 text-sm">Last Updated: January 27, 2025</p>
            </div>

            <div className="space-y-8 text-white/80 leading-relaxed">
              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">1. Introduction</h2>
                <p>
                  Veridian Vanguard Advisors ("we," "our," or "us") is committed to protecting the privacy and confidentiality 
                  of our clients and website visitors. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                  your information when you visit our website or engage our advisory services.
                </p>
                <p>
                  Given the high-profile nature of our clientele, we maintain the highest standards of data protection and 
                  confidentiality in accordance with applicable laws including GDPR, CCPA, and other relevant regulations.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">2. Information We Collect</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">2.1 Personal Information</h3>
                <p>We may collect the following types of personal information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Contact information (name, email address, organization/affiliation)</li>
                  <li>Professional information (business details, service interests)</li>
                  <li>Communication preferences and inquiry details</li>
                  <li>Information provided during consultations or advisory engagements</li>
                </ul>

                <h3 className="text-xl font-medium text-lime-400 mt-6">2.2 Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>IP address and device information</li>
                  <li>Browser type and version</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">3. How We Use Your Information</h2>
                <p>We use collected information for the following purposes:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>To respond to inquiries and provide requested advisory services</li>
                  <li>To communicate about our services, insights, and updates</li>
                  <li>To improve our website functionality and user experience</li>
                  <li>To analyze website traffic and usage patterns</li>
                  <li>To comply with legal obligations and protect our rights</li>
                  <li>To prevent fraud and maintain security</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">4. Cookies and Tracking Technologies</h2>
                <p>
                  We use cookies and similar tracking technologies to enhance your browsing experience. You can control 
                  cookie preferences through our cookie consent banner or your browser settings. Essential cookies are 
                  necessary for website functionality, while analytics cookies help us improve our services.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">5. Information Sharing and Disclosure</h2>
                <p>
                  We maintain strict confidentiality and do not sell, trade, or rent your personal information. 
                  We may share information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Service Providers:</strong> Trusted third-party vendors who assist in website operations and service delivery</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
                  <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                  <li><strong>Protection of Rights:</strong> To protect our rights, property, or safety, or that of our clients</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">6. Data Security</h2>
                <p>
                  We implement industry-standard security measures to protect your information, including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure server infrastructure and access controls</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Employee training on data protection and confidentiality</li>
                  <li>Strict confidentiality agreements with service providers</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">7. Your Rights and Choices</h2>
                <p>Depending on your jurisdiction, you may have the following rights:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Access:</strong> Request access to your personal information</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                  <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                  <li><strong>Restriction:</strong> Request restriction of processing activities</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, please contact us at connect@veridianvanguard.com
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">8. International Data Transfers</h2>
                <p>
                  As a global advisory firm operating across multiple jurisdictions, your information may be transferred 
                  to and processed in countries other than your country of residence. We ensure appropriate safeguards 
                  are in place for such transfers in compliance with applicable data protection laws.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">9. Data Retention</h2>
                <p>
                  We retain personal information for as long as necessary to fulfill the purposes outlined in this policy, 
                  comply with legal obligations, resolve disputes, and enforce our agreements. Client advisory records are 
                  retained in accordance with professional standards and regulatory requirements.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">10. Children's Privacy</h2>
                <p>
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal 
                  information from children. If you believe we have collected information from a child, please contact us 
                  immediately.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">11. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. 
                  We will notify you of material changes by posting the updated policy on our website with a revised 
                  "Last Updated" date.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">12. Contact Information</h2>
                <p>
                  For questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-white/5 p-6 rounded-lg border border-lime-400/20 mt-4">
                  <p><strong>Veridian Vanguard Advisors</strong></p>
                  <p>Email: connect@veridianvanguard.com</p>
                  <p>Location: Los Angeles, CA</p>
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-black py-12 mt-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center space-x-8 text-sm">
                <a href="/privacy-policy" className="text-lime-400 hover:text-lime-300 transition-colors underline">Privacy Policy</a>
                <a href="/terms-of-service" className="text-gray-400 hover:text-white transition-colors underline">Terms of Service</a>
                <a href="/disclaimers" className="text-gray-400 hover:text-white transition-colors underline">Disclaimers</a>
                <a href="https://readdy.ai/?ref=logo" className="text-gray-400 hover:text-lime-400 transition-colors underline">Powered by Readdy</a>
              </div>
              <div className="text-xs text-gray-500">
                © 2025 Veridian Vanguard Advisors. All Rights Reserved.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicy;
